import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import {
  useCreateForumThreadMutation,
  useForumCategoriesQuery,
  FORUM_CATEGORIES,
} from "../api/forumApi";
import { AttachmentPicker } from "../components/Attachments";
import { PrimaryButton, SecondaryButton, NAVY, GOLD } from "../components/People";

export default function NewThreadPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  // General, because a composer that opens with nothing chosen is a form
  // that can be filled in completely and still refused.
  const [category, setCategory] = useState("general");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  // The server's list where it has arrived, the built-in copy until then.
  // Only the labels differ between them; the keys are the same.
  const { data: catalogue } = useForumCategoriesQuery();
  const categories = catalogue?.categories ?? FORUM_CATEGORIES;

  const [create, { isLoading }] = useCreateForumThreadMutation();

  const submit = async () => {
    setError(null);
    if (!title.trim() || !body.trim()) {
      setError("A discussion needs a subject and something to discuss.");
      return;
    }

    try {
      const thread = await create({
        title: title.trim(),
        body: body.trim(),
        category,
        files,
      }).unwrap();
      navigate(`/forum/${thread.id}`);
    } catch (e: any) {
      setError(e?.data?.message || e?.error || "The discussion could not be posted.");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <button
        type="button"
        onClick={() => navigate("/forum")}
        className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.15em] text-gray-400 hover:text-gray-600 mb-5"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to the forum
      </button>

      <h1 className="text-xl font-black text-gray-900 tracking-tight">New discussion</h1>
      <p className="text-xs text-gray-500 mt-2 mb-6">
        Visible to the National Policy Committee only.
      </p>

      <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm space-y-5">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-gray-500 mb-2">
            Subject
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            disabled={isLoading}
            placeholder="What is this about?"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-400"
          />
        </div>

        <div>
          <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-gray-500 mb-2">
            Category
          </label>
          {/* Laid out rather than hidden in a select: the blurb under each
              label is what stops everything being filed under General. */}
          <div className="grid gap-2 sm:grid-cols-2">
            {categories.map((c) => {
              const on = category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  disabled={isLoading}
                  onClick={() => setCategory(c.key)}
                  className="rounded-xl border px-4 py-3 text-left transition-colors"
                  style={{
                    borderColor: on ? GOLD : "#E5E7EB",
                    backgroundColor: on ? "#FDF8F0" : "white",
                  }}
                >
                  <span className="block text-[13px] font-black" style={{ color: NAVY }}>
                    {c.label}
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-gray-500">
                    {c.blurb}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-gray-500 mb-2">
            Your post
          </label>
          {/* Plain text on purpose. A rich-text body would have to be stored
              as HTML and rendered back with dangerouslySetInnerHTML, which
              turns every member of the committee into someone who can run
              script in every other member's browser. Line breaks are kept. */}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={isLoading}
            rows={12}
            placeholder="Set out the matter…"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-gray-400"
          />
        </div>

        <AttachmentPicker files={files} onChange={setFiles} disabled={isLoading} />

        {error && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3">
            <p className="text-xs font-bold text-rose-700">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <SecondaryButton onClick={() => navigate("/forum")} disabled={isLoading}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={submit} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Post discussion
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
